import { Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';

import { combineLatest, Observable, Subscription } from 'rxjs';

import { Study } from '../../../../../models/study';
import {
  PRIVILEGE_ACTIONS,
  PRIVILEGE_SECTIONS,
  STUDY_PARTICIPANT_GROUPS,
} from '../../../../../core/dashboard-constants';
import { AppState } from '../../../../../state';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import {
  selectIsLoadingSelectedStudy,
  selectSelectedStudy,
  selectSelectedStudyLoaded,
} from '../../../../state/study/study.selectors';
import {
  selectIsLoadingTiimStudyParticipants,
  selectIsSingleParticipantVisible,
  selectTiimStudyParticipants,
  selectTiimStudyParticipantsLoaded,
} from '../../../../state/study-participant/tiim-study-participant/tiim-study-participant.selectors';
import { TiimStudyParticipant } from '../../../../../models/tiim-study-participant';
import { Intervention } from '../../../../../models/intervention';
import { openDashboardConfirmationDialog } from '../../../../state/dashboard.actions';
import {
  deleteTiimStudyParticipant,
  setSelectedTiimStudyParticipant,
  setSingleTiimStudyParticipantPageVisibility,
} from '../../../../state/study-participant/tiim-study-participant/tiim-study-participant.actions';
import { getParticipantScreeningResponses } from '../../../../state/metrics/metrics.actions';
import { selectInterventions } from '../../../../state/intervention/intervention.selectors';

interface TiimStudyParticipantCategory {
  name?: string;
  interventionId?: number;
  rejected?: boolean;
  unsubscribed?: boolean;
}

@Component({
  selector: 'dashboard-tiim-study-participants',
  templateUrl: './tiim-study-participants.component.html',
  styleUrls: ['./tiim-study-participants.component.scss'],
})
export class TiimStudyParticipantsComponent implements OnInit, OnDestroy {
  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;
  PARTICIPANT_GROUPS = STUDY_PARTICIPANT_GROUPS;

  loadingSelectedStudy$: Observable<boolean>;
  selectedStudyLoaded$: Observable<boolean>;

  selectedStudy$: Observable<Study>;
  selectedInterventions$: Observable<Intervention[]>;

  loadingTiimStudyParticipants$: Observable<boolean>;
  tiimStudyParticipantsLoaded$: Observable<boolean>;

  singleTiimStudyParticipantVisible$: Observable<boolean>;

  subscriptions = new Subscription();

  selectedStudy: Study;
  selectedInterventions: Intervention[];

  tiimStudyParticipants: TiimStudyParticipant[];

  tiimStudyParticipantsCategories: TiimStudyParticipantCategory[];

  constructor(private store: Store<AppState>, public privilegeService: PrivilegeService) {}

  ngOnInit(): void {
    this.loadingSelectedStudy$ = this.store.select(selectIsLoadingSelectedStudy);
    this.selectedStudyLoaded$ = this.store.select(selectSelectedStudyLoaded);

    this.loadingTiimStudyParticipants$ = this.store.select(selectIsLoadingTiimStudyParticipants);
    this.tiimStudyParticipantsLoaded$ = this.store.select(selectTiimStudyParticipantsLoaded);

    this.singleTiimStudyParticipantVisible$ = this.store.select(selectIsSingleParticipantVisible);

    this.selectedStudy$ = this.store.select(selectSelectedStudy);
    this.selectedInterventions$ = this.store.select(selectInterventions);

    this.subscriptions.add(
      combineLatest([this.selectedInterventions$, this.selectedStudy$]).subscribe((data) => {
        this.selectedInterventions = data[0];
        this.selectedStudy = data[1];

        if (data[0]?.length) {
          this.tiimStudyParticipantsCategories = this.getTiimStudyParticipantsCategories(this.selectedInterventions);
        } else if (data[1]?.interventions?.length) {
          this.tiimStudyParticipantsCategories = this.getTiimStudyParticipantsCategories(
            this.selectedStudy.interventions
          );
        }
      })
    );

    this.subscriptions.add(
      this.store
        .select(selectTiimStudyParticipants)
        .subscribe((tiimStudyParticipants) => (this.tiimStudyParticipants = tiimStudyParticipants))
    );
  }

  getTiimStudyParticipantsCategories(interventions: Intervention[]): TiimStudyParticipantCategory[] {
    const categories = [
      { name: this.PARTICIPANT_GROUPS.UNASSIGNED, interventionId: 0, rejected: false, unsubscribed: false },
    ];

    if (interventions) {
      interventions.forEach((intervention: Intervention) =>
        categories.push({
          name: intervention.name,
          interventionId: intervention.id,
          rejected: false,
          unsubscribed: false,
        })
      );
    }
    categories.push({
      name: this.PARTICIPANT_GROUPS.REJECTED,
      interventionId: 0,
      rejected: true,
      unsubscribed: false,
    });
    categories.push({
      name: this.PARTICIPANT_GROUPS.UNSUBSCRIBED,
      interventionId: 0,
      rejected: false,
      unsubscribed: true,
    });
    return categories;
  }

  getCategoryTiimStudyParticipants(category: TiimStudyParticipantCategory): TiimStudyParticipant[] {
    const categoryTiimStudyParticipants = [];

    if (category.name === this.PARTICIPANT_GROUPS.UNSUBSCRIBED) {
      this.tiimStudyParticipants.forEach((tiimStudyParticipant: TiimStudyParticipant) => {
        if (!!tiimStudyParticipant.unsubscribed === true) {
          categoryTiimStudyParticipants.push(tiimStudyParticipant);
        }
      });
    } else {
      this.tiimStudyParticipants.forEach((tiimStudyParticipant: TiimStudyParticipant) => {
        const interventionId = tiimStudyParticipant.interventionId || 0;
        if (
          interventionId === category.interventionId &&
          !!tiimStudyParticipant.rejected === category.rejected &&
          !!tiimStudyParticipant.unsubscribed === false
        ) {
          categoryTiimStudyParticipants.push(tiimStudyParticipant);
        }
      });
    }

    return categoryTiimStudyParticipants;
  }

  deleteTiimStudyParticipant(studyId: number, participantId: number, tiimStudyParticipantId: number): void {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: 'Are you sure you want to delete this participant?',
          onConfirm: deleteTiimStudyParticipant({
            studyId,
            participantId,
            tiimStudyParticipantId,
          }),
        },
      })
    );
  }

  openParticipantDetails(selectedTiimStudyParticipant: TiimStudyParticipant): void {
    this.store.dispatch(setSelectedTiimStudyParticipant({ selectedTiimStudyParticipant }));
    this.store.dispatch(
      setSingleTiimStudyParticipantPageVisibility({
        singleParticipantVisible: true,
      })
    );
    this.store.dispatch(
      getParticipantScreeningResponses({
        studyId: this.selectedStudy?.id,
        participantId: selectedTiimStudyParticipant?.participantId,
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
